/**
 * useOfertaPt — el «Plano Google Maps 7 Dias» para usuarios pt, visto desde la SPA.
 *
 * Quién lo tiene y hasta cuándo lo decide el backend (`QR/billing/oferta_pt.py`)
 * y llega en el `me` de gbusiness, que todas las páginas ya piden. Aquí solo se
 * añade el RELOJ: `activa` se recalcula cada segundo contra `cierreAt`, de modo
 * que al cerrar la oferta el popup y la cápsula con cuenta atrás se apagan
 * solos, sin recargar. El checkout no se fía de esto — vuelve a preguntar al
 * servidor —, así que un reloj del navegador desajustado solo cambia lo que se
 * PINTA.
 *
 * La oferta es el ANUAL (`ofertaAplicaA`), con los locales que tenga: el primero
 * a 287 € y el resto a precio de tramo.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getGbusinessMe } from '@/api/gbusinessService';
import { ME_KEY } from '@/hooks/useOnboarding';

const pad = (n) => String(n).padStart(2, '0');

/** Milisegundos que quedan hasta `iso`, refrescado cada segundo. */
export function useMsRestantes(iso) {
  const deadline = iso ? Date.parse(iso) : NaN;
  const [now, setNow] = useState(() => Date.now());
  const corriendo = Number.isFinite(deadline) && deadline > now;
  useEffect(() => {
    if (!corriendo) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [corriendo]);
  return Number.isFinite(deadline) ? Math.max(0, deadline - now) : 0;
}

/** `3d 04:12:09`, o `04:12:09` el último día. */
export function formatoCuentaAtras(ms) {
  const total = Math.floor(Math.max(0, ms) / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const reloj = `${pad(h)}:${pad(m)}:${pad(s)}`;
  return d > 0 ? `${d}d ${reloj}` : reloj;
}

/** ¿El checkout con este periodo entra en la oferta? Solo el anual. */
export function ofertaAplicaA(billing) {
  return billing === 'annual';
}

export function useOfertaPt({ enabled = true } = {}) {
  // `enabled`: la tarjeta de precios se monta también sin sesión, y un GET de
  // `me` sin cookie es un 401 que `httpClient` convierte en un salto a /login.
  const { data: me } = useQuery({
    queryKey: ME_KEY, queryFn: getGbusinessMe, retry: false, enabled,
  });
  const oferta = me?.ofertaPt || null;
  const ms = useMsRestantes(oferta?.activa ? oferta.cierreAt : null);
  const activa = !!oferta?.activa && ms > 0;
  return {
    elegible: !!oferta?.elegible,
    pendientePopup: !!oferta?.pendientePopup && activa,
    activa,
    plazasRestantes: oferta?.plazasRestantes ?? null,
    plazasTotal: oferta?.plazasTotal ?? 50,
    msRestantes: ms,
    cuentaAtras: formatoCuentaAtras(ms),
  };
}
